import React, { FC, useEffect, useMemo, useState } from "react";
import { useStore } from "effector-react";
import { ActivityCard } from "../../../cards";
import {
  $selectedActivities,
  selectedActivitiesApi,
  SelectedActivity,
} from "../../../store/selected/selectedActivities";
import { $selectedLocation } from "../../../store/selected/selectedLocations";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Carousel,
  Grid,
  Input,
  Pagination,
  Skeleton,
} from "../../../components";
import { editActivity } from "./EditActivity";
import { ActivitiesEndpoint } from "../../../store/endpoints/activities";
import { $selectedDates } from "../../../store/selected/selectedDateRange";
import {
  $selectedPersonTypes,
  $selectedPersonTypesQty,
} from "../../../store/selected/selectedPersonTypes";
import { AppConfig } from "../../../config";
import { isArrayFilled, isPresent } from "../../../utils";
import { useDebounce, usePageSize } from "../../../hooks";
import { Activity } from "../../../store/models";
import { Dinero } from "dinero.js";
import { GridBreakpoints } from "../../../components/Grid";
import { FiSearch } from "react-icons/fi";
import stl from "../DateRange/index.module.scss";

export type ActivitiesDisplayData = (Activity & {
  selectedData?: SelectedActivity;
  pricingData?: {
    max: Dinero;
    min: Dinero;
  };
})[];

const gridBreakpoints: GridBreakpoints = {
  lg: 4,
  md: 6,
  sm: 6,
  xs: 12,
};

const Activities: FC = () => {
  const { t, i18n } = useTranslation(AppConfig.translationFileNames.step_Main);
  const pageSize = usePageSize();

  const { data: activities, pending } = useStore(ActivitiesEndpoint.list.store);
  const { data: priceRanges, pending: priceRangesPending } = useStore(
    ActivitiesEndpoint.priceRanges.store
  );
  const [paginationCurrentPage, setPaginationCurrentPage] = useState<number>(1);

  const selectedActivities = useStore($selectedActivities);
  const selectedLocation = useStore($selectedLocation);
  const selectedDates = useStore($selectedDates);
  const selectedPersonTypes = useStore($selectedPersonTypes);
  const selectedPersonTypesQty = useStore($selectedPersonTypesQty);

  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValue, 1000);

  const [viewMode, setViewMode] = useState<"pages" | "carousel">(
    pageSize.md ? "pages" : "carousel"
  );

  useEffect(() => {
    setViewMode(pageSize.md ? "pages" : "carousel");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize.md]);

  const read = () => {
    if (
      selectedLocation &&
      isArrayFilled(selectedDates) &&
      isArrayFilled(selectedPersonTypes)
    ) {
      ActivitiesEndpoint.list.call({
        _query: {
          lang: i18n.language,
          location_id: selectedLocation.id,
          dates: selectedDates,
          person_types: selectedPersonTypes,
          search: searchValue,
        },
      });
    }
  };

  useEffect(() => {
    const handlerTimeout = setTimeout(() => {
      read();
      changePage(1);
    }, 1000); //debounce for read() & changePage()
    return () => {
      ActivitiesEndpoint.list.cancel();
      clearTimeout(handlerTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, i18n.language, selectedDates, selectedPersonTypes]);

  useEffect(() => {
    const interval = setInterval(read, AppConfig.forceReloadListItemsByTime);
    return () => clearInterval(interval);
  }, [selectedLocation, selectedDates, selectedPersonTypes]);

  useEffect(() => {
    read();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, pageSize.md]);

  useEffect(() => {
    if (
      isPresent(activities) &&
      isArrayFilled(activities.data) &&
      isArrayFilled(selectedDates) &&
      isArrayFilled(selectedPersonTypes)
    ) {
      const handlerTimeout = setTimeout(
        () =>
          ActivitiesEndpoint.priceRanges.call({
            activities: activities.data,
            personTypes: selectedPersonTypes,
            dates: selectedDates,
          }),
        2000
      );

      return () => {
        ActivitiesEndpoint.priceRanges.cancel();
        clearTimeout(handlerTimeout);
      };
    }
  }, [activities, selectedPersonTypes, selectedDates]);

  const active = useMemo<boolean>(() => {
    return selectedPersonTypesQty > 0 && isArrayFilled(selectedDates);
  }, [selectedPersonTypesQty, selectedDates]);

  const changePage = (page: number) => {
    setPaginationCurrentPage(page);
  };

  /** Sublist items packages */
  const displayData = useMemo<ActivitiesDisplayData>(() => {
    if (!activities || !active) return [];

    const indexOfFirstDisplayData =
      (paginationCurrentPage - 1) * AppConfig.paginationPerPage;
    const indexOfLastDisplayData =
      indexOfFirstDisplayData + AppConfig.paginationPerPage;

    return (viewMode === "pages"
      ? activities.data.slice(indexOfFirstDisplayData, indexOfLastDisplayData)
      : activities.data
    ).map((item) => {
      const selectedPackage = selectedActivities.find(
        ({ entity: { id } }) => id === item.id
      );

      return {
        ...item,
        selectedData: selectedPackage,
        pricingData: priceRanges?.[item.id],
      };
    });
  }, [
    activities,
    selectedActivities,
    active,
    priceRanges,
    paginationCurrentPage,
    viewMode,
  ]);

  return (
    <>
      <Grid
        container
        gutter={15}
        style={{ alignItems: "center", marginTop: "3em", marginBottom: "1em" }}
      >
        <Grid item xs={12} sm={4}>
          <h2
            style={{
              marginBottom: "0.5em",
              ...(active ? {} : { opacity: 0.5 }),
            }}
          >
            {selectedLocation?.activities_title}
          </h2>
        </Grid>

        {active && (
          <Grid item xs={12} sm={4}>
            <Input
              disabled={pending}
              prepend={
                <FiSearch
                  style={{
                    fontSize: "1.1em",
                    margin: "4px 0 0 8px",
                  }}
                />
              }
              className={stl.searchBar}
              variant={"search"}
              type={"search"}
              size={"small"}
              value={searchValue}
              onChange={setSearchValue}
              placeholder={t("searchBarPlaceholder")}
            />
          </Grid>
        )}
        {viewMode === "pages" &&
          active &&
          activities?.data?.length !== undefined &&
          activities?.data?.length > AppConfig.paginationPerPage && (
            <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
              <Pagination
                disabled={pending}
                showLessItems
                total={activities?.data?.length ?? 0}
                current={paginationCurrentPage}
                pageSize={AppConfig.paginationPerPage}
                onChange={changePage}
              />
            </Grid>
          )}

        <Grid item xs={12} style={{ margin: "1.5em 0" }}>
          {selectedLocation?.activities_description}
        </Grid>
      </Grid>

      {pending && (
        <Skeleton height={250} showIcon>
          {t("activityModal.retrievingTimeSlots")}
        </Skeleton>
      )}

      {!pending && active && viewMode === "pages" && (
        <Grid container gutter={0}>
          {displayData.map((item, idx) => (
            <Grid key={item?.name ?? idx} item {...gridBreakpoints}>
              <ActivityCard
                disabled={selectedPersonTypesQty === 0}
                data={item}
                onEdit={editActivity}
                onDelete={({ entity: { id } }) => {
                  selectedActivitiesApi.remove({ id });
                }}
                pricePending={priceRangesPending}
                price={item.pricingData}
                selectedData={item.selectedData}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {!pending &&
        active &&
        viewMode === "carousel" &&
        isArrayFilled(displayData) && (
          <Carousel
            style={{ marginLeft: "" }}
            carouselProps={{
              selectedQuantity: selectedActivities.length,
              perPageLoad: 9999,
            }}
          >
            {displayData.map((item, idx) => (
              <ActivityCard
                key={item?.name ?? idx}
                disabled={selectedPersonTypesQty === 0}
                data={item}
                onEdit={editActivity}
                onDelete={({ entity: { id } }) => {
                  selectedActivitiesApi.remove({ id });
                }}
                pricePending={priceRangesPending}
                price={item.pricingData}
                selectedData={item.selectedData}
              />
            ))}
          </Carousel>
        )}
      {active && !isArrayFilled(displayData) && !searchValue && (
        <p className={stl.noSlots}>{t("noSlots")}</p>
      )}
      {searchValue && !isArrayFilled(displayData) && !pending && (
        <Alert variant={"info"}>{t("nothingFound")}</Alert>
      )}
    </>
  );
};

export default Activities;
